interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  schema?: Record<string, unknown>;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  schema,
}: SEOProps) {
  const defaultTitle = '年轻的母亲 - 成长与育儿的温暖指南';
  const defaultDescription =
    '「年轻的母亲」专为年轻妈妈打造，提供科学育儿知识、心理调适建议、产后恢复技巧与亲子关系构建方案。';
  const defaultKeywords =
    '年轻的母亲,年轻妈妈育儿指南,新手妈妈心理调适,产后恢复方法,亲子关系建立技巧,年轻妈妈自我提升';

  const pageTitle = title ? `${title} - 年轻的母亲` : defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;
  const pageUrl = canonicalUrl || 'https://youngmom.readdy.ai/';

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
    </>
  );
}

export function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '年轻的母亲',
    url: 'https://youngmom.readdy.ai/',
    description:
      '专为年轻妈妈打造的育儿成长平台，提供科学育儿知识、心理调适建议、产后恢复技巧与亲子关系构建方案。',
    sameAs: [],
  };
}

export function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '年轻的母亲',
    url: 'https://youngmom.readdy.ai/',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://youngmom.readdy.ai/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
}

export function getBreadcrumbSchema(
  items: { name: string; url: string }[],
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function getArticleSchema(article: {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  url: string;
  authorName?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    datePublished: article.datePublished,
    url: article.url,
    author: {
      '@type': 'Person',
      name: article.authorName || '年轻的母亲编辑团队',
    },
    publisher: {
      '@type': 'Organization',
      name: '年轻的母亲',
    },
  };
}

export function getFAQSchema(
  faqs: { question: string; answer: string }[],
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}